import { useState, useMemo, useCallback } from 'react';
import { HierarchyBuilder } from './components/HierarchyBuilder';
import { PairwiseComparison } from './components/PairwiseComparison';
import { ProjectEvaluation } from './components/ProjectEvaluation';
import { ResultsView } from './components/ResultsView';
import { calculateAHP, buildComparisonMatrix } from './utils/ahp';
import type { AppPhase, Criterion, Project, PairwiseVote } from './types';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { type ToolItem } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import { StackRankingLive } from '../../components/prioritization/session/liveExperience/liveTools/StackRankingLive';

function AHPContent(props: ToolHostProps) {
  const { isLiveSession } = useLiveToolSession();
  const [liveRanking, setLiveRanking] = useState<string[]>([]);
  
  const [phase, setPhase] = useState<AppPhase>('setup');
  const [goal, setGoal] = useState('');
  const [criteria, setCriteria] = useState<Criterion[]>([]);
  const [criteriaVotes, setCriteriaVotes] = useState<PairwiseVote[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  
  // Track source IDs for imported items
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});

  // Handler for importing items from workspace (these become projects to evaluate)
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newProjects: Project[] = toolItems.map((item) => ({
      id: item.id,
      name: item.name,
      description: item.description,
      scores: {},
      cost: 1, // Default cost
    }));
    
    // Track source IDs
    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach(item => {
      if (item.sourceId) {
        newSourceIdMap[item.id] = item.sourceId;
      }
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));
    
    setProjects(prev => {
      const merged = [...prev, ...newProjects];
      if (isLiveSession) {
        setLiveRanking(merged.map(p => p.id));
      }
      return merged;
    });
  }, [isLiveSession]);

  // Calculate criteria weights from pairwise comparisons
  const criteriaWeights = useMemo(() => {
    if (criteriaVotes.length === 0) return null;

    const topLevelCriteria = criteria.filter(c => c.parentId === null);
    if (topLevelCriteria.length < 2) return null;

    try {
      const itemNames = topLevelCriteria.map(c => c.name);
      const matrix = buildComparisonMatrix(itemNames, criteriaVotes);
      const result = calculateAHP(matrix);
      
      // Update criteria with calculated weights
      const updatedCriteria = criteria.map(c => {
        if (c.parentId === null) {
          const index = topLevelCriteria.findIndex(tc => tc.id === c.id);
          return {
            ...c,
            localWeight: result.weights[index],
            globalWeight: result.weights[index],
          };
        }
        return c;
      });

      return { result, updatedCriteria };
    } catch (error) {
      console.error('Error calculating weights:', error);
      return null;
    }
  }, [criteria, criteriaVotes]);

  // Update criteria with calculated weights
  const weightedCriteria = useMemo(() => {
    if (!criteriaWeights) return criteria;
    return criteriaWeights.updatedCriteria;
  }, [criteria, criteriaWeights]);

  // Calculate final project scores
  const scoredProjects = useMemo(() => {
    const topLevelCriteria = weightedCriteria.filter(c => c.parentId === null);
    
    return projects.map(project => {
      let finalScore = 0;
      
      topLevelCriteria.forEach(criterion => {
        const score = project.scores[criterion.id] || 0;
        finalScore += criterion.globalWeight * score;
      });

      const efficiencyScore = project.cost > 0 ? finalScore / project.cost : 0;

      return {
        ...project,
        finalScore,
        efficiencyScore,
      };
    });
  }, [projects, weightedCriteria]);

  // Get results for saving as session result
  const handleGetResults = useCallback((): ToolResultItem[] => {
    const sortedProjects = [...scoredProjects].sort((a, b) => b.finalScore - a.finalScore);
    
    return sortedProjects.map((project, idx) => ({
      id: sourceIdMap[project.id] || project.id,
      name: project.name,
      rank: idx + 1,
      score: project.finalScore,
      metadata: {
        finalScore: project.finalScore,
        efficiencyScore: project.efficiencyScore,
        cost: project.cost,
        scores: project.scores,
      }
    }));
  }, [scoredProjects, sourceIdMap]);

  const handleSetupComplete = () => {
    setPhase('weighting');
  };

  const handleWeightingComplete = () => {
    if (criteriaWeights?.result.is_consistent) {
      setPhase('evaluation');
    }
  };

  const handleEvaluationComplete = () => {
    setPhase('results');
  };

  const handleReset = () => {
    setPhase('setup');
    setGoal('');
    setCriteria([]);
    setCriteriaVotes([]);
    setProjects([]);
    setSourceIdMap({});
  };

  const topLevelCriteria = criteria.filter(c => c.parentId === null);
  const criteriaNames = topLevelCriteria.map(c => c.name);

  const liveRankingReady =
    isLiveSession && projects.length > 0 && liveRanking.length === projects.length;

  const handleLiveGetResults = useCallback((): ToolResultItem[] => {
    return liveRanking.map((id, idx) => {
      const project = projects.find(p => p.id === id);
      return {
        id: sourceIdMap[id] || id,
        name: project?.name || id,
        rank: idx + 1,
        score: projects.length - idx,
      };
    });
  }, [liveRanking, projects, sourceIdMap]);

  return (
    <ToolWrapper
      toolName="AHP"
      toolDescription="Make complex decisions using structured pairwise comparisons"
      onImportItems={handleImportItems}
      onGetResults={
        liveRankingReady ? handleLiveGetResults : phase === 'results' ? handleGetResults : undefined
      }
      itemCount={projects.length}
      {...props}
    >
      {isLiveSession && projects.length > 0 && (
        <LiveToolScaffold
          title="AHP — quick rank"
          subtitle="Drag to rank alternatives (full AHP available outside live sessions)"
          progressDone={liveRanking.length}
          progressTotal={projects.length}
        >
          <StackRankingLive
            items={projects.map(p => ({ id: p.id, name: p.name, description: p.description }))}
            ranking={liveRanking}
            onRankingChange={setLiveRanking}
          />
        </LiveToolScaffold>
      )}

      {!isLiveSession && (
      <div className="min-h-screen bg-[var(--surface)] py-8">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          {/* Items imported indicator */}
          {projects.length > 0 && phase === 'setup' && (
            <div className="mb-6 p-3 bg-[var(--green-dim)] border border-[var(--green)]">
              <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                <strong>{projects.length} items imported</strong> from workspace. 
                They will be evaluated after you set up your criteria.
              </p>
            </div>
          )}

          {/* Phase Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {(['setup', 'weighting', 'evaluation', 'results'] as AppPhase[]).map((p, index) => (
                <div key={p} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        phase === p
                          ? 'bg-consulting-slate-blue-600 text-white'
                          : index < (['setup', 'weighting', 'evaluation', 'results'] as AppPhase[]).indexOf(phase)
                          ? 'bg-[var(--green)] text-white'
                          : 'bg-[var(--rule-bright)] text-[var(--text-sec)]'
                      }`}
                    >
                      {index + 1}
                    </div>
                    <div className="mt-2 text-xs font-medium text-[var(--text-sec)] capitalize text-center">
                      {p === 'setup' ? 'Setup' : p === 'weighting' ? 'Weight' : p === 'evaluation' ? 'Evaluate' : 'Results'}
                    </div>
                  </div>
                  {index < 3 && (
                    <div
                      className={`h-1 flex-1 mx-2 ${
                        index < (['setup', 'weighting', 'evaluation', 'results'] as AppPhase[]).indexOf(phase)
                          ? 'bg-[var(--green)]'
                          : 'bg-[var(--rule-bright)]'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8">
            {phase === 'setup' && (
              <HierarchyBuilder
                goal={goal}
                onGoalChange={setGoal}
                criteria={criteria}
                onCriteriaChange={setCriteria}
                onNext={handleSetupComplete}
              />
            )}

            {phase === 'weighting' && (
              <PairwiseComparison
                items={criteriaNames}
                context={`in the context of "${goal}"`}
                votes={criteriaVotes}
                onVoteChange={setCriteriaVotes}
                onBack={() => setPhase('setup')}
                onNext={handleWeightingComplete}
              />
            )}

            {phase === 'evaluation' && (
              <ProjectEvaluation
                projects={projects}
                onProjectsChange={setProjects}
                criteria={weightedCriteria}
                onBack={() => setPhase('weighting')}
                onNext={handleEvaluationComplete}
              />
            )}

            {phase === 'results' && (
              <ResultsView
                projects={scoredProjects}
                criteria={weightedCriteria}
                goal={goal}
                onReset={handleReset}
              />
            )}
          </div>

          {/* Info Box */}
          <div className="mt-8 bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
            <h3 className="font-semibold text-[var(--text-sec)] mb-2">About AHP</h3>
            <p className="text-sm text-[var(--text-sec)]">
              The Analytic Hierarchy Process (AHP) is a structured technique for organizing and analyzing complex decisions. 
              It uses pairwise comparisons to derive priority weights and includes consistency checking to validate your judgments. 
              This tool implements Saaty's AHP methodology with real-time consistency feedback.
            </p>
          </div>
        </div>
      </div>
      )}
    </ToolWrapper>
  );
}

export default function AHP(props: ToolHostProps) {
  return <AHPContent {...props} />;
}
